import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import publicService from '../services/publicService';

// Shop-wide settings the admin controls, fetched once when the app mounts.
// Everything downstream reads the mode from here rather than from a value
// baked into the bundle, so flipping a toggle in the admin panel is enough.
const SettingsContext = createContext(null);

// What the storefront assumes when the settings cannot be read: the on-site
// cart, which is how the shop behaved before WhatsApp ordering existed.
const FALLBACK_SETTINGS = {
  whatsapp_ordering_enabled: false,
  whatsapp_number: '',
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const loaded = await publicService.getSettings();
        if (active) {
          setSettings({ ...FALLBACK_SETTINGS, ...loaded });
        }
      } catch (err) {
        if (active) {
          setSettings(FALLBACK_SETTINGS);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => {
    const whatsappOrdering = !loading && !!settings.whatsapp_ordering_enabled;

    return {
      settings,
      loading,
      whatsappOrdering,
      whatsappNumber: settings.whatsapp_number || '',
      // Deliberately false while loading too: neither mode commits until the
      // settings land, so no cart icon flashes up and then vanishes.
      cartEnabled: !loading && !settings.whatsapp_ordering_enabled,
    };
  }, [settings, loading]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);

  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }

  return context;
};

export default SettingsContext;
